import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getVariantsByGroup, exportVariantsByGroup, importVariants, downloadVariantsTemplate } from '../../api/variants';
import { GroupInterface, VariantInterface } from '../../types';
import VariantsTable from './VariantsTable';
import ImportErrorModal from '../ImportErrorModal';
import CreateVariantModal from './CreateVariantModal';

interface GroupVariantsModalProps {
  open: boolean;
  group: GroupInterface | null;
  onClose: () => void;
  onRefresh?: () => void;
}

function GroupVariantsModal({ open, group, onClose, onRefresh }: GroupVariantsModalProps): React.JSX.Element {
  const [variants, setVariants] = useState<VariantInterface[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [createVariantModalOpen, setCreateVariantModalOpen] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [importErrors, setImportErrors] = useState<any[]>([]);
  const [importErrorsModalOpen, setImportErrorsModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && group) {
      setPage(1); // Reset to first page when group changes
    }
  }, [open, group]);

  useEffect(() => {
    if (open && group) {
      fetchVariants(page);
    }
    // eslint-disable-next-line
  }, [open, group, page, searchQuery, sortBy, sortOrder]);

  const fetchVariants = async (pageNum: number): Promise<void> => {
    if (!group) return;
    setLoading(true);
    let queryParams = `page=${pageNum}&page_size=${pageSize}&unavailable=true`;
    if (searchQuery) {
      queryParams += `&method=search&name=${encodeURIComponent(searchQuery)}`;
    }
    if (sortBy) {
      queryParams += `&sort_by=${sortBy}&sort_order=${sortOrder}`;
    }
    const res = await getVariantsByGroup(group.id, queryParams);
    setVariants(res.data || []);
    setTotal(res.total || 0);
    setTotalPages(res.total_pages || 1);
    setLoading(false);
  };

  const handleExport = async () => {
    if (!group) return;
    let queryParams = `page=1&page_size=${total || 100}`;
    if (searchQuery) {
      queryParams += `&method=search&name=${encodeURIComponent(searchQuery)}`;
    }
    if (sortBy) {
      queryParams += `&sort_by=${sortBy}&sort_order=${sortOrder}`;
    }
    await exportVariantsByGroup(group.id, queryParams);
  };

  const handleImport = async (data: any[]) => {
    try {
      await importVariants(data);
      alert('הייבוא הושלם בהצלחה!');
      fetchVariants(page);
    } catch (err: any) {
      if (err.errors) {
        setImportErrors(err.errors);
        setImportErrorsModalOpen(true);
      } else {
        alert(err.message || 'שגיאה בייבוא הנתונים');
      }
    }
  };

  const handleSortChange = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleResetSearch = () => {
    setSearchQuery('');
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const handleOpenCreateVariantModal = (): void => {
    setCreateVariantModalOpen(true);
  };

  const handleCloseCreateVariantModal = (): void => {
    setCreateVariantModalOpen(false);
  };

  const handleVariantClick = (variant: VariantInterface) => {
    navigate(`/contacts?method=search&variant_id=${variant.id}`);
  };

  if (!group) return <></>;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{group.name} - קורסים</span>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <VariantsTable
            variants={variants}
            onAddVariant={handleOpenCreateVariantModal}
            onVariantClick={handleVariantClick}
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={handlePageChange}
            onRefresh={() => {
              fetchVariants(page);
              onRefresh?.();
            }}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            onSearch={setSearchQuery}
            onResetSearch={handleResetSearch}
            initialSearchQuery={searchQuery}
            onExport={handleExport}
            onImport={handleImport}
            onDownloadTemplate={downloadVariantsTemplate}
            loading={loading}
          />
        </DialogContent>
      </Dialog>
      <ImportErrorModal
        open={importErrorsModalOpen}
        onClose={() => setImportErrorsModalOpen(false)}
        errors={importErrors}
      />

      <CreateVariantModal
        open={createVariantModalOpen}
        onClose={handleCloseCreateVariantModal}
        group={group}
        onVariantCreated={() => fetchVariants(page)}
      />
    </>
  );
}

export default GroupVariantsModal; 